import {LABELS} from '@github-ui/item-picker-base/constants/labels'
import {LazyItemPicker} from '@github-ui/item-picker-base/LazyItemPicker'
import {useItemPickerErrorFallback} from '@github-ui/item-picker-base/useItemPickerErrorFallback'
import {IssueTypePickerBase, type IssueTypePickerBaseProps} from '@github-ui/item-picker-issue-type'
import {QueryClient, useQueryClient, type QueryPersister} from '@github-ui/react-query'
import {IS_SERVER} from '@github-ui/ssr-utils'
import {useEffect, useRef, useState} from 'react'
import {
  apiGetSearchIssueTypes,
  buildItemPickerIssueTypeSearchQueryKey,
  transformSearchIssueTypeResponseToQueryData,
  type IssueTypePickerQueryData,
} from './api/api-get-search-issue-types'
import {parseGraphQLResponse} from '@github-ui/item-picker-base/api/graphql'
import {useAnalytics} from '@github-ui/use-analytics'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {createIssueTypePersister} from './api/issue-type-persister'

type IssueTypeQueryKey = ReturnType<typeof buildItemPickerIssueTypeSearchQueryKey>

//
// ItemPicker <== ItemPickerWrapper (internal) <== ItemPickerFetcher (internal) <== IssueTypePicker (exported)
//                                                                              <== BulkIssueIssueTypePicker (exported)

export async function fetchIssueTypes(
  queryClient: QueryClient,
  {
    owner,
    query,
    count,
    persister,
  }: {
    owner: string
    query: string
    count?: number
    persister?: QueryPersister<IssueTypePickerQueryData, IssueTypeQueryKey>
  },
): Promise<IssueTypePickerQueryData> {
  const response = await queryClient.fetchQuery({
    queryKey: buildItemPickerIssueTypeSearchQueryKey({owner, query, count}),
    queryFn: async () => {
      const responseJson = await apiGetSearchIssueTypes({owner, query, count})
      const responseData = parseGraphQLResponse(responseJson)
      return transformSearchIssueTypeResponseToQueryData(responseData.data)
    },
    persister,
  })
  return response
}

export type IssueTypePickerProps = IssueTypePickerBaseProps & {
  owner: string
}

function ItemPickerFetcher({owner, ...rest}: IssueTypePickerProps) {
  const [isLoading, setIsLoading] = useState(false)
  const [fetchKey, setFetchKey] = useState(0)
  const [isError, setIsError] = useState(false)
  const [data, setData] = useState<IssueTypePickerQueryData | null>(null)
  const queryClient = useQueryClient()
  const {sendAnalyticsEvent} = useAnalytics()

  useEffect(() => {
    if (!IS_SERVER) {
      const fetchInitialIssueTypes = async () => {
        try {
          setIsLoading(true)
          const persister: QueryPersister<IssueTypePickerQueryData, IssueTypeQueryKey> = async (
            queryFn,
            context,
            query,
          ) => {
            if (!isFeatureEnabled('issue_type_picker_api_caching')) {
              // call the original queryFn, making the persister a no-op
              return queryFn(context)
            }
            // lazy load the persister to prevent typebox library from being put into the issue type picker javascript chunk
            const issueTypePersister = createIssueTypePersister(sendAnalyticsEvent)
            return issueTypePersister(queryFn, context, query)
          }

          const response = await fetchIssueTypes(queryClient, {
            owner,
            query: '',
            persister,
          })
          setData(response)
          setIsLoading(false)
          setIsError(false)
        } catch {
          setIsError(true)
          setIsLoading(false)
        }
      }
      // This useEffect is run to populate the initial list of issue types to show in the picker.
      // It does not take a query into account and only relies on owner
      // This useEffect has the `fetchInitialIssueTypes` callback (which has `owner`)
      // and `fetchKey` as its keys, so it will only be executed if one of those dependencies changes
      // - not whenever the filter changes.
      fetchInitialIssueTypes()
    }
  }, [fetchKey, owner, queryClient, sendAnalyticsEvent])

  const anchorRef = useRef<HTMLButtonElement>(null)

  const {createFallbackComponent} = useItemPickerErrorFallback({
    errorMessage: LABELS.cantEditItems('types'),
    anchorElement: anchorProps => rest.anchorElement(anchorProps, anchorRef),
    open: true,
  })

  if (isError) {
    return createFallbackComponent(() => setFetchKey(current => current + 1))
  }

  return <IssueTypePickerBase issueTypes={data?.issueTypes ?? []} isLoading={isLoading} {...rest} />
}

export function IssueTypePicker({owner, anchorElement, readonly, activeIssueType, ...rest}: IssueTypePickerProps) {
  return (
    <LazyItemPicker
      keybindingCommandId="item-pickers:open-issue-type"
      anchorElement={(anchorProps, ref) => anchorElement(anchorProps, ref)}
      createChild={() => (
        <ItemPickerFetcher
          owner={owner}
          readonly={readonly}
          activeIssueType={activeIssueType}
          triggerOpen
          anchorElement={anchorElement}
          {...rest}
        />
      )}
      insidePortal={rest.insidePortal}
    />
  )
}

try{ ItemPickerFetcher.displayName ||= 'ItemPickerFetcher' } catch {}
try{ IssueTypePicker.displayName ||= 'IssueTypePicker' } catch {}